import React, { useMemo, useEffect, useRef } from "react";
import {
  CloseIcon,
  HistoryIcon,
} from "components/Icons";
import { reportTypeItems } from './reportTypes';
import NavBar from "components/Navigations/NavBar/NavBar";
import UIDateRangePicker from "components/form/UIDateRangePicker/UIDateRangePicker.component";
import { Button } from 'components/form/Button';
import DropDown from "components/Dropdown/Dropdown.component";
import { useResearchContext } from "../../ResearchContext.provider";
import { parse, format, isValid } from 'date-fns';
import './ResearchNavBar.component.scss';
import ConversationHistory from "../ConversationHistory/ConversationHistory.component";

export type ResearchNavBarProps = {
  userType: 'demo' | 'regular';
};

const ResearchNavBar = ({ userType }: ResearchNavBarProps) => {
  const { state, actions } = useResearchContext();
  const { longReportType, showConversationHistory, historyLoading, startLiteDataDate, endLiteDataDate } = state;
  const lockedDateTypes: string[] = ['swotReport', 'pestlReport'];
  const isDateLocked = lockedDateTypes.includes(longReportType);

  const getTooltipText = () => {
    if (longReportType === 'swotReport') return "Date selection is not available for SWOT Analysis";
    if (longReportType === 'pestlReport') return "Date selection is not available for PESTL Analysis";
    return "See the latest insights";
  };

  const handleDateRangeChange = ([startDate, endDate]: [Date, Date]) => {
    if (isValid(startDate) && isValid(endDate)) {
      actions.setMultiple({
        startLiteDataDate: format(startDate, 'yyyy-MM-dd'),
        endLiteDataDate: format(endDate, 'yyyy-MM-dd'),
        userDateOverride: true
      });
    }
  };

  const historyButtonRef = React.useRef<HTMLButtonElement>(null);
  const [dateHighlight, setDateHighlight] = React.useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setDateHighlight(true);
    const t = setTimeout(() => setDateHighlight(false), 2200);
    return () => clearTimeout(t);
  }, [startLiteDataDate, endLiteDataDate]);

  const handleConversationHistoryClick = () => {
    if (actions.setMultiple) actions.setMultiple({ showConversationHistory: !showConversationHistory });
    if (!showConversationHistory && actions.getConversationHistory) actions.getConversationHistory();
  };

  const handleOptionChange = (item: { value: string }) => {
    window.open(`/research?t=${encodeURIComponent(item.value)}`, '_blank');
  };

  const startDates = useMemo(() => {
    return [
      parse(startLiteDataDate, 'yyyy-MM-dd', new Date()),
      parse(endLiteDataDate, 'yyyy-MM-dd', new Date())
    ];
  }, [startLiteDataDate, endLiteDataDate]);

  return (
    <NavBar
      theme="dark"
      logo={{ small: false, desktop: false }}
      isDemo={(userType === 'demo')}
      withoutNavigation={false}
      iconMenuItems={[]}
      leftContent={
        <div className='research-form-options'>
          <DropDown value={longReportType} itemList={reportTypeItems} onChange={handleOptionChange} className="framework-dropdown" data-testid="report-type-dropdown" data-tooltip="Choose your analysis framework" />
          <div className={`report-date-picker-wrapper${isDateLocked ? ' is-locked' : ''}${dateHighlight ? ' date-updated' : ''}`} data-tooltip={getTooltipText()}>
            <UIDateRangePicker
              onChange={handleDateRangeChange}
              className="report-date-range-picker"
              placeholder="Select Date Range"
              value={startDates}
              cleanable={false}
              format="MM/dd/yyyy"
              data-testid="report-date-selector"
              disabled={isDateLocked}
            />
          </div>
          <div className={`report-date-picker-wrapper mobile${isDateLocked ? ' is-locked' : ''}`} data-tooltip={getTooltipText()}>
            <UIDateRangePicker
              onChange={handleDateRangeChange}
              placeholder="Select Date Range"
              value={startDates}
              cleanable={false}
              className="report-date-range-picker-mobile"
              data-testid="report-date-selector"
              format="MM/dd/yyyy"
              block
              showOneCalendar
              placement="bottomStart"
              disabled={isDateLocked}
            />
          </div>
          <div className="conversation-history-wrapper">
            <Button
              ref={historyButtonRef}
              icon={showConversationHistory ? CloseIcon : HistoryIcon}
              variant={!showConversationHistory ? "primary" : "danger"}
              size="medium"
              iconPosition="left"
              isLoading={historyLoading}
              onClick={handleConversationHistoryClick}
              aria-label="History"
              data-tooltip={showConversationHistory ? 'Hide History' : 'Revisit your past reports'}
              data-title="Conversation History"
            >
              History
            </Button>
            <ConversationHistory toggleRef={historyButtonRef as React.RefObject<HTMLElement>} />
          </div>
        </div>
      }
    />
  );
};

export default ResearchNavBar;
